import type { ChangePasswordInterface } from "types/profile";

export type PasswordFieldName = keyof ChangePasswordInterface;

export type PasswordConstraint = {
    key: string;
    labelKey: string;
    valid: boolean;
};

export type FormatMessage = (id: string) => string;

type PasswordRule = {
    fails: () => boolean;
    messageKey: string;
};

export const getPasswordConstraints = (password: string): PasswordConstraint[] => [
    {
        key: "min",
        labelKey: "profile.settings.security.password.constraint.min",
        valid: password.length >= 8,
    },
    {
        key: "letter",
        labelKey: "profile.settings.security.password.constraint.letter",
        valid: /[A-Za-z]/.test(password),
    },
    {
        key: "mixed",
        labelKey: "profile.settings.security.password.constraint.mixed",
        valid: /[a-z]/.test(password) && /[A-Z]/.test(password),
    },
    {
        key: "number",
        labelKey: "profile.settings.security.password.constraint.number",
        valid: /\d/.test(password),
    },
    {
        key: "symbol",
        labelKey: "profile.settings.security.password.constraint.symbol",
        valid: /[^A-Za-z0-9]/.test(password),
    },
];

const firstErrorKey = (rules: PasswordRule[]) =>
    rules.find((rule) => rule.fails())?.messageKey;

const getPasswordErrorKey = (password: string, requiredMessageKey: string) =>
    firstErrorKey([
        {
            fails: () => !password,
            messageKey: requiredMessageKey,
        },
        {
            fails: () => password.length > 255,
            messageKey: "profile.settings.security.password.error.max_length",
        },
        {
            fails: () => getPasswordConstraints(password).some((constraint) => !constraint.valid),
            messageKey: "profile.settings.security.password.error.constraints",
        },
    ]);

export const createChangePasswordValidator =
    (formatMessage: FormatMessage) =>
    (values: Partial<ChangePasswordInterface>) => {
        const errors: Partial<Record<PasswordFieldName, string>> = {};
        const newPassword = values.new_password ?? "";
        const confirmPassword = values.confirm_password ?? "";
        const newPasswordErrorKey = getPasswordErrorKey(
            newPassword,
            "profile.settings.security.password.error.new_required"
        );
        const confirmPasswordErrorKey = firstErrorKey([
            {
                fails: () => !confirmPassword,
                messageKey: "profile.settings.security.password.error.confirm_required",
            },
            {
                fails: () => confirmPassword.length > 255,
                messageKey: "profile.settings.security.password.error.max_length",
            },
            {
                fails: () => confirmPassword !== newPassword,
                messageKey: "profile.settings.security.password.error.confirm_mismatch",
            },
            {
                fails: () => getPasswordConstraints(confirmPassword).some((constraint) => !constraint.valid),
                messageKey: "profile.settings.security.password.error.constraints",
            },
        ]);

        if (!values.current_password) {
            errors.current_password = formatMessage("profile.settings.security.password.error.current_required");
        }

        if (newPasswordErrorKey) {
            errors.new_password = formatMessage(newPasswordErrorKey);
        }

        if (confirmPasswordErrorKey) {
            errors.confirm_password = formatMessage(confirmPasswordErrorKey);
        }

        return errors;
    };
